import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { NavLink, Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  Activity,
  BarChart3,
  Building2,
  ChevronDown,
  ChevronRight,
  Cpu,
  CreditCard,
  Flag,
  Globe,
  Inbox,
  LogOut,
  MessageSquare,
  Moon,
  PanelLeft,
  PanelRightClose,
  PanelRightOpen,
  ScrollText,
  Search,
  Settings,
  ShieldCheck,
  Sun,
  Terminal,
  UserCheck,
  Users,
  Workflow,
} from 'lucide-react';
import { useAgentStore } from '../stores/agent';
import { useFeatureFlagsStore } from '../stores/feature-flags';
import { useCapacity } from '../lib/capacity';
import { ActiveStack } from './ActiveStack';
import { AgentPresence } from './AgentPresence';
import { NotificationBell } from './NotificationBell';
import { BrandLogo } from './BrandLogo';
import { IncomingCallBanner } from './IncomingCallBanner';
import { resolveAvatarUrl } from '../lib/server-origin';
import { reconnectBus, useBusConnection } from '../lib/bus-connection';
import { useVisitorsStore } from '../stores/visitors';
import { useConversationsStore } from '../stores/conversations';
import { isVisitorLive } from '../lib/visitor-live';
import { setLiveIndicators } from '../lib/live-indicators';
import { readTheme, setTheme as applyAndPersistTheme, type Theme } from '../lib/theme';
import { ensurePushSubscription } from '../lib/push';
import {
  Button,
  Badge,
  Avatar,
  AvatarImage,
  AvatarFallback,
  Switch,
  Separator,
  CommandDialog,
  type CommandItem,
} from './ui';
import styles from './Shell.module.css';

const RAIL_KEY = 'prakashsoftware.agent.active-rail-collapsed.v1';
function readRailCollapsed(): boolean {
  try {
    return localStorage.getItem(RAIL_KEY) === '1';
  } catch {
    return false;
  }
}
function saveRailCollapsed(v: boolean): void {
  try {
    localStorage.setItem(RAIL_KEY, v ? '1' : '0');
  } catch {
    /* non-fatal */
  }
}

const SIDEBAR_KEY = 'openline.agent.sidebar-collapsed.v1';
function readSidebarCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === '1';
  } catch {
    return false;
  }
}
function saveSidebarCollapsed(v: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_KEY, v ? '1' : '0');
  } catch {
    /* non-fatal */
  }
}

function getPageTitle(pathname: string): string {
  if (pathname.startsWith('/dashboard')) return 'Live Monitor';
  if (pathname.startsWith('/conversation')) return 'Active Chat';
  if (pathname.startsWith('/tickets')) return 'Tickets';
  if (pathname.startsWith('/contacts')) return 'Contacts';
  if (pathname.startsWith('/analytics')) return 'Analytics';
  if (pathname.startsWith('/commands')) return 'Canned Commands';
  if (pathname.startsWith('/settings')) return 'Preferences';
  if (pathname.startsWith('/admin/flags')) return 'Feature Flags';
  if (pathname.startsWith('/admin/sites')) return 'Sites & Embeds';
  if (pathname.startsWith('/admin/agents')) return 'Agents & Access';
  if (pathname.startsWith('/admin/billing')) return 'Plan & Billing';
  if (pathname.startsWith('/admin/accounts')) return 'Accounts & Tenants';
  if (pathname.startsWith('/admin/system')) return 'System Engine';
  if (pathname.startsWith('/admin/whatsapp')) return 'WhatsApp Gateway';
  if (pathname.startsWith('/admin/logs')) return 'Platform Logs';
  if (pathname.startsWith('/admin/jobs')) return 'Background Jobs';
  if (pathname.startsWith('/admin')) return 'Admin Console';
  return 'Dashboard';
}

export function Shell() {
  const agent = useAgentStore((s) => s.current);
  const availability = useAgentStore((s) => s.availability);
  const acceptsVideo = useAgentStore((s) => s.acceptsVideo);
  const setAvailability = useAgentStore((s) => s.setAvailability);
  const setAcceptsVideo = useAgentStore((s) => s.setAcceptsVideo);
  const logout = useAgentStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const capacity = useCapacity();
  const busState = useBusConnection();
  const flags = useFeatureFlagsStore((s) => s.flags);
  const busDown = busState === 'closed' || busState === 'disconnected';

  // Left sidebar collapse & mobile drawer
  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => readSidebarCollapsed());
  const [mobileOpen, setMobileOpen] = useState(false);

  function toggleSidebar() {
    if (window.innerWidth <= 900) {
      setMobileOpen((prev) => !prev);
    } else {
      setSidebarCollapsed((prev) => {
        const next = !prev;
        saveSidebarCollapsed(next);
        return next;
      });
    }
  }

  // Account/status dropdown anchored on the user card
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!userMenuOpen) return;
    function onDocPointer(e: MouseEvent) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', onDocPointer);
    return () => document.removeEventListener('mousedown', onDocPointer);
  }, [userMenuOpen]);

  // Notifications
  useEffect(() => {
    void ensurePushSubscription();
  }, []);

  // Theme toggle
  const [theme, setThemeState] = useState<Theme>(() => readTheme());
  const toggleTheme = useCallback(() => {
    const next: Theme = theme === 'dark' ? 'light' : 'dark';
    setThemeState(next);
    applyAndPersistTheme(next);
  }, [theme]);

  // Global Command Palette (Ctrl+K / Cmd+K)
  const [commandOpen, setCommandOpen] = useState(false);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setCommandOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Active rail collapse state
  const [railCollapsed, setRailCollapsed] = useState<boolean>(() => readRailCollapsed());
  const snapshotRef = useRef<Set<string>>(new Set());
  const visitorRows = useVisitorsStore((s) => s.rows);
  const [hasNewLive, setHasNewLive] = useState(false);

  // Live visitor count
  const [liveTick, setLiveTick] = useState(0);
  useEffect(() => {
    const id = window.setInterval(() => setLiveTick((t) => (t + 1) % 1_000_000), 5_000);
    return () => window.clearInterval(id);
  }, []);
  const liveCount = useMemo(() => {
    const now = Date.now();
    let n = 0;
    for (const row of visitorRows.values()) if (isVisitorLive(row, now)) n += 1;
    return n;
    // liveTick only re-runs this so Date.now() moves on without a store change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visitorRows, liveTick]);

  useEffect(() => {
    setLiveIndicators(liveCount);
  }, [liveCount]);
  useEffect(() => () => setLiveIndicators(0), []);

  useEffect(() => {
    if (!railCollapsed) {
      setHasNewLive(false);
      return;
    }
    const now = Date.now();
    for (const row of visitorRows.values()) {
      if (!isVisitorLive(row, now)) continue;
      if (!snapshotRef.current.has(row.identity.visitorId)) {
        setHasNewLive(true);
        return;
      }
    }
  }, [railCollapsed, visitorRows]);

  // Active conversations count
  const activeCount = useConversationsStore((s) => {
    let count = 0;
    for (const c of s.byId.values()) {
      if (!s.dismissed.has(c.sessionId)) count += 1;
    }
    return count;
  });

  const anyInVideo = useConversationsStore((s) => {
    for (const c of s.byId.values()) if (c.inVideo) return true;
    return false;
  });

  function toggleRail() {
    setRailCollapsed((prev) => {
      const next = !prev;
      saveRailCollapsed(next);
      if (next) {
        const live = new Set<string>();
        const now = Date.now();
        for (const row of visitorRows.values()) {
          if (isVisitorLive(row, now)) live.add(row.identity.visitorId);
        }
        snapshotRef.current = live;
        setHasNewLive(false);
      } else {
        snapshotRef.current = new Set();
        setHasNewLive(false);
      }
      return next;
    });
  }

  // Close mobile sidebar on route transition
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // Computed before the signed-out early return: hooks must run on every render.
  const isSuperAdmin = agent?.role === 'super-admin';
  const isAccountAdmin = agent?.role === 'account-admin';
  const hasAdminAccess = isSuperAdmin || isAccountAdmin;
  const currentPageTitle = getPageTitle(location.pathname);

  const commandItems: CommandItem[] = useMemo(() => [
    { id: 'dash', label: 'Live Monitor (Visitors & Map)', category: 'Navigation', icon: <Activity size={16} />, shortcut: 'G D', onSelect: () => navigate('/dashboard') },
    ...((!flags || flags.tickets) ? [{ id: 'tickets', label: 'Tickets Inbox', category: 'Navigation', icon: <Inbox size={16} />, shortcut: 'G T', onSelect: () => navigate('/tickets') }] : []),
    { id: 'contacts', label: 'Contacts & CRM', category: 'Navigation', icon: <Users size={16} />, shortcut: 'G C', onSelect: () => navigate('/contacts') },
    { id: 'commands', label: 'Canned Slash Commands', category: 'Navigation', icon: <Terminal size={16} />, shortcut: 'G /', onSelect: () => navigate('/commands') },
    { id: 'analytics', label: 'Analytics & Insights', category: 'Navigation', icon: <BarChart3 size={16} />, shortcut: 'G A', onSelect: () => navigate('/analytics') },
    { id: 'settings', label: 'Agent Preferences', category: 'Navigation', icon: <Settings size={16} />, shortcut: 'G S', onSelect: () => navigate('/settings') },
    ...(hasAdminAccess ? [
      { id: 'admin-sites', label: 'Admin: Sites & Embeds', category: 'Administration', icon: <Globe size={16} />, onSelect: () => navigate('/admin/sites') },
      { id: 'admin-agents', label: 'Admin: Agents & Access', category: 'Administration', icon: <UserCheck size={16} />, onSelect: () => navigate('/admin/agents') },
      { id: 'admin-billing', label: 'Admin: Plan & Billing', category: 'Administration', icon: <CreditCard size={16} />, onSelect: () => navigate('/admin/billing') },
    ] : []),
    ...(isSuperAdmin ? [
      { id: 'admin-flags', label: 'Platform: Feature Flags', category: 'Platform', icon: <Flag size={16} />, onSelect: () => navigate('/admin/flags') },
      { id: 'admin-accounts', label: 'Platform: Accounts & Tenants', category: 'Platform', icon: <Building2 size={16} />, onSelect: () => navigate('/admin/accounts') },
      { id: 'admin-system', label: 'Platform: System & AI Engine', category: 'Platform', icon: <Cpu size={16} />, onSelect: () => navigate('/admin/system') },
      { id: 'admin-whatsapp', label: 'Platform: WhatsApp Gateway', category: 'Platform', icon: <MessageSquare size={16} />, onSelect: () => navigate('/admin/whatsapp') },
      { id: 'admin-logs', label: 'Platform: Wire Logs Stream', category: 'Platform', icon: <ScrollText size={16} />, onSelect: () => navigate('/admin/logs') },
      { id: 'admin-jobs', label: 'Platform: Background Jobs', category: 'Platform', icon: <Workflow size={16} />, onSelect: () => navigate('/admin/jobs') },
    ] : []),
    { id: 'toggle-theme', label: `Switch to ${theme === 'dark' ? 'Light' : 'Dark'} Mode`, category: 'Preferences', icon: theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />, onSelect: toggleTheme },
    { id: 'avail-online', label: 'Set Status: Available (Online)', category: 'Presence', icon: <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#10b981', display: 'inline-block' }} />, onSelect: () => setAvailability('available') },
    { id: 'avail-away', label: 'Set Status: Away', category: 'Presence', icon: <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#f59e0b', display: 'inline-block' }} />, onSelect: () => setAvailability('away') },
    { id: 'avail-offline', label: 'Set Status: Offline', category: 'Presence', icon: <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#64748b', display: 'inline-block' }} />, onSelect: () => setAvailability('offline') },
  ], [hasAdminAccess, isSuperAdmin, navigate, theme, toggleTheme, setAvailability, flags]);

  if (!agent) {
    navigate('/login', { replace: true });
    return null;
  }


  return (
    <div
      className={styles.shell}
      data-sidebar-collapsed={sidebarCollapsed ? 'true' : 'false'}
      data-sidebar-open={mobileOpen ? 'true' : 'false'}
    >
      {/* Mobile Drawer Backdrop */}
      <div className={styles.mobileOverlay} onClick={() => setMobileOpen(false)} aria-hidden />

      {/* ============================================================
          1. shadcn Left Sidebar
          ============================================================ */}
      <aside className={styles.sidebar} aria-label="Dashboard Sidebar">
        {/* Brand Header */}
        <div className={styles.sidebarHeader}>
          <NavLink to="/dashboard" className={styles.sidebarBrand} title="Openline Dashboard">
            <div className={styles.sidebarLogoBox}>
              <BrandLogo variant="icon" height={20} />
            </div>
            <div className={styles.sidebarBrandInfo}>
              <span className={styles.sidebarBrandTitle}>Openline</span>
              <span className={styles.sidebarBrandMeta}>Live Support · v2.4</span>
            </div>
          </NavLink>
        </div>

        {/* Quick Search / Command Palette Trigger */}
        <button
          type="button"
          className={styles.sidebarSearchBtn}
          onClick={() => setCommandOpen(true)}
          title="Search or run command (Ctrl+K)"
        >
          <Search size={14} />
          <span className={styles.sidebarSearchLabel}>Search commands...</span>
          <kbd className={styles.sidebarSearchKbd}>⌘K</kbd>
        </button>

        {/* Navigation Content */}
        <div className={styles.sidebarNavScroll}>
          {/* Operations Group */}
          <div className={styles.sidebarGroup}>
            <div className={styles.sidebarGroupLabel}>Operations</div>
            <div className={styles.sidebarGroupItems}>
              <NavLink
                to="/dashboard"
                className={({ isActive }) =>
                  [styles.navItem, isActive ? styles.navItemActive : ''].join(' ')
                }
                title="Live Monitor"
              >
                <Activity size={16} className={styles.navItemIcon} />
                <span className={styles.navItemLabel}>Live Monitor</span>
                {liveCount > 0 && (
                  <span className={styles.navItemBadge}>
                    <span className={styles.badgePulseDot} />
                    {liveCount} live
                  </span>
                )}
              </NavLink>

              {(!flags || flags.tickets) && (
                <NavLink
                  to="/tickets"
                  className={({ isActive }) =>
                    [styles.navItem, isActive ? styles.navItemActive : ''].join(' ')
                  }
                  title="Tickets"
                >
                  <Inbox size={16} className={styles.navItemIcon} />
                  <span className={styles.navItemLabel}>Tickets</span>
                </NavLink>
              )}

              <NavLink
                to="/contacts"
                className={({ isActive }) =>
                  [styles.navItem, isActive ? styles.navItemActive : ''].join(' ')
                }
                title="Contacts"
              >
                <Users size={16} className={styles.navItemIcon} />
                <span className={styles.navItemLabel}>Contacts</span>
              </NavLink>

              <NavLink
                to="/commands"
                className={({ isActive }) =>
                  [styles.navItem, isActive ? styles.navItemActive : ''].join(' ')
                }
                title="Canned Commands"
              >
                <Terminal size={16} className={styles.navItemIcon} />
                <span className={styles.navItemLabel}>Commands</span>
              </NavLink>
            </div>
          </div>

          {/* Insights Group */}
          <div className={styles.sidebarGroup}>
            <div className={styles.sidebarGroupLabel}>Insights</div>
            <div className={styles.sidebarGroupItems}>
              <NavLink
                to="/analytics"
                className={({ isActive }) =>
                  [styles.navItem, isActive ? styles.navItemActive : ''].join(' ')
                }
                title="Analytics"
              >
                <BarChart3 size={16} className={styles.navItemIcon} />
                <span className={styles.navItemLabel}>Analytics</span>
              </NavLink>
            </div>
          </div>

          {/* Administration Group — Parent navigation item */}
          {hasAdminAccess && (
            <div className={styles.sidebarGroup}>
              <div className={styles.sidebarGroupLabel}>Administration</div>
              <div className={styles.sidebarGroupItems}>
                <NavLink
                  to="/admin/sites"
                  className={({ isActive }) =>
                    [
                      styles.navItem,
                      isActive || location.pathname.startsWith('/admin') ? styles.navItemActive : '',
                    ].join(' ')
                  }
                  title="Admin Console"
                >
                  <ShieldCheck size={16} className={styles.navItemIcon} />
                  <span className={styles.navItemLabel}>Admin Console</span>
                </NavLink>
              </div>
            </div>
          )}

          {/* Preferences Group */}
          <div className={styles.sidebarGroup}>
            <div className={styles.sidebarGroupLabel}>Preferences</div>
            <div className={styles.sidebarGroupItems}>
              <NavLink
                to="/settings"
                className={({ isActive }) =>
                  [styles.navItem, isActive ? styles.navItemActive : ''].join(' ')
                }
                title="Preferences & Profile"
              >
                <Settings size={16} className={styles.navItemIcon} />
                <span className={styles.navItemLabel}>Settings</span>
              </NavLink>
            </div>
          </div>
        </div>

        {/* Sidebar Footer (Agent Profile with shadcn Avatar) */}
        <div className={styles.sidebarFooter} ref={userMenuRef}>
          <button
            type="button"
            className={styles.userCardBtn}
            onClick={() => setUserMenuOpen((o) => !o)}
            aria-haspopup="menu"
            aria-expanded={userMenuOpen}
            title={`${agent.name} (${availability})`}
          >
            <div className={styles.userAvatarWrap}>
              <Avatar size="sm">
                {agent.avatarUrl ? (
                  <AvatarImage src={resolveAvatarUrl(agent.avatarUrl)} alt={agent.name} />
                ) : null}
                <AvatarFallback>{agent.initials}</AvatarFallback>
              </Avatar>
              <span className={styles.presenceDot} data-availability={availability} aria-hidden />
            </div>
            <div className={styles.userInfo}>
              <span className={styles.userName}>{agent.name}</span>
              <span className={styles.userRole}>
                {availability} · {agent.role.replace('-', ' ')}
              </span>
            </div>
            <ChevronDown size={14} className={styles.userCardCaret} aria-hidden />
          </button>

          {/* User Popover Menu */}
          {userMenuOpen && (
            <div className={styles.userPopover} role="menu">
              <div className={styles.popoverHeader}>
                <span className={styles.popoverUserTitle}>{agent.name}</span>
                <span className={styles.popoverUserEmail}>{agent.email}</span>
              </div>

              <Separator />

              <div className={styles.popoverSection}>
                <span className={styles.popoverLabel}>Availability</span>
                <div className={styles.availabilityButtonGroup}>
                  <button
                    type="button"
                    className={[styles.availOption, availability === 'available' ? styles.availOptionActive : ''].join(' ')}
                    onClick={() => setAvailability('available')}
                  >
                    <span className={styles.availDotGreen} />
                    <span>Online</span>
                  </button>
                  <button
                    type="button"
                    className={[styles.availOption, availability === 'away' ? styles.availOptionActive : ''].join(' ')}
                    onClick={() => setAvailability('away')}
                  >
                    <span className={styles.availDotYellow} />
                    <span>Away</span>
                  </button>
                  <button
                    type="button"
                    className={[styles.availOption, availability === 'offline' ? styles.availOptionActive : ''].join(' ')}
                    onClick={() => setAvailability('offline')}
                  >
                    <span className={styles.availDotGray} />
                    <span>Offline</span>
                  </button>
                </div>
              </div>

              <Separator />

              <div className={styles.popoverSection}>
                <div className={styles.popoverRow}>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <span className={styles.popoverLabel}>In-Page Video Calls</span>
                    <span className={styles.popoverHelpText}>
                      {capacity.videoStatus === 'on_call' ? 'Currently on call' : acceptsVideo ? 'Ready for calls' : 'Calls paused'}
                    </span>
                  </div>
                  <Switch
                    checked={acceptsVideo}
                    disabled={capacity.videoStatus === 'on_call'}
                    onCheckedChange={(checked) => setAcceptsVideo(checked)}
                    aria-label="Accept incoming video calls"
                  />
                </div>
              </div>

              <Separator />

              <button
                type="button"
                className={styles.popoverActionBtn}
                onClick={() => {
                  setUserMenuOpen(false);
                  navigate('/settings');
                }}
              >
                <Settings size={14} />
                <span>Preferences &amp; Profile</span>
              </button>

              <button
                type="button"
                className={styles.popoverActionBtn}
                onClick={toggleTheme}
              >
                {theme === 'dark' ? <Sun size={14} /> : <Moon size={14} />}
                <span>{theme === 'dark' ? 'Light mode' : 'Dark mode'}</span>
              </button>

              <Separator />

              <button
                type="button"
                className={styles.popoverSignoutBtn}
                onClick={() => {
                  setUserMenuOpen(false);
                  logout();
                }}
              >
                <LogOut size={14} />
                <span>Sign out</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {/* ============================================================
          2. Main Viewport Area (Inset)
          ============================================================ */}
      <div className={styles.mainViewport}>
        {/* Top Header Bar */}
        <header className={styles.topHeader}>
          <div className={styles.topHeaderLeft}>
            <Button
              variant="ghost"
              size="icon"
              className={styles.topSidebarToggle}
              onClick={toggleSidebar}
              title="Toggle sidebar"
              aria-label="Toggle sidebar"
            >
              <PanelLeft size={16} />
            </Button>
            <div className={styles.topSeparator} />
            <nav className={styles.breadcrumb} aria-label="Breadcrumb">
              <span>Openline</span>
              <ChevronRight size={13} className={styles.breadcrumbSep} />
              <span className={styles.breadcrumbCurrent}>{currentPageTitle}</span>
            </nav>
          </div>

          <div className={styles.topHeaderCenter}>
            <button
              type="button"
              className={styles.topCommandTrigger}
              onClick={() => setCommandOpen(true)}
              title="Quick search or jump to page (Ctrl+K)"
            >
              <Search size={13} />
              <span>Search commands, pages, tickets...</span>
              <kbd>⌘K</kbd>
            </button>
          </div>

          <div className={styles.topHeaderRight}>
            {/* Bus status indicator */}
            {busDown ? (
              <Badge
                variant="destructive"
                className={styles.busBadge}
                onClick={() => reconnectBus()}
                style={{ cursor: 'pointer' }}
                title="Live bus disconnected — click to reconnect"
              >
                <span className={styles.busDot} />
                BUS OFFLINE
              </Badge>
            ) : busState === 'connecting' ? (
              <Badge variant="outline" className={styles.busBadge} title="Connecting to live bus…">
                <span className={styles.busDot} />
                CONNECTING
              </Badge>
            ) : (
              <Badge variant="success" className={styles.busBadge} title="Bus connected and live">
                <span className={styles.busDot} />
                LIVE
              </Badge>
            )}

            {/* Notification Bell */}
            <NotificationBell />

            {/* Agent presence telemetry */}
            <AgentPresence />

            {/* Active chats toggle */}
            <Button
              variant={railCollapsed ? 'outline' : 'secondary'}
              size="sm"
              className={styles.topBtn}
              onClick={toggleRail}
              title={railCollapsed ? 'Show active chats panel' : 'Hide active chats panel'}
            >
              <MessageSquare size={14} />
              <span>Chats</span>
              {activeCount > 0 && (
                <Badge variant="default" className={styles.activeChatsBadge}>
                  {activeCount}
                </Badge>
              )}
            </Button>

            {/* Theme switcher */}
            <Button
              variant="ghost"
              size="icon"
              className={styles.topBtnIconOnly}
              onClick={toggleTheme}
              title={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
              aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
            >
              {theme === 'dark' ? <Sun size={15} /> : <Moon size={15} />}
            </Button>
          </div>
        </header>

        {/* Content Body with Outlet + Right Active Rail */}
        <div className={styles.contentArea} data-rail-collapsed={railCollapsed ? 'true' : 'false'}>
          <main className={styles.mainContent}>
            <Outlet />
          </main>

          {/* Right rail for active chats */}
          {anyInVideo ? null : railCollapsed ? (
            <aside className={styles.activeRailCollapsed} aria-label="Active conversations (collapsed)">
              <button
                type="button"
                className={styles.railToggle}
                onClick={toggleRail}
                title={hasNewLive ? 'New live visitors arrived — click to open' : 'Show active chats'}
                aria-label="Show active chats"
              >
                <PanelRightOpen size={16} aria-hidden />
                {hasNewLive ? <span className={styles.railToggleBlink} aria-hidden /> : null}
              </button>
              <span className={styles.railCollapsedLabel} aria-hidden>
                Active
              </span>
            </aside>
          ) : (
            <aside className={styles.activeRail} aria-label="Active conversations">
              <button
                type="button"
                className={styles.railToggleInline}
                onClick={toggleRail}
                title="Hide active chats"
                aria-label="Hide active chats"
              >
                <PanelRightClose size={15} aria-hidden />
              </button>
              <ActiveStack />
            </aside>
          )}
        </div>
      </div>

      <IncomingCallBanner />

      {/* Global Command Palette */}
      <CommandDialog
        open={commandOpen}
        onOpenChange={setCommandOpen}
        items={commandItems}
        placeholder="Type a command or jump to page..."
      />
    </div>
  );
}
